// src/components/ChatPanel/index.tsx
import { useRef, useEffect, useState } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import { Send, Lock, CheckCircle2, Square } from 'lucide-react';
import { ChatMarkdown } from 'components/ChatMarkdown';
import ObfuscationIndicator from 'components/ObfuscationIndicator';
import { formatChatMessageTime } from 'utils/dateUtils';
import type { ChatMsg } from 'types';
import type { SessionStatus } from 'scenarios';

interface ChatPanelProps {
  sessionStatus: SessionStatus;
  messages: ChatMsg[];
  streamingMessage: ChatMsg | null;
  displayedContent: string;
  progressLabel: string | null;
  isLoading: boolean;
  onSendMessage: (text: string) => Promise<void>;
  onStopGeneration?: () => void;
  scenarioId: string;
  modelName?: string | null;
  /** Идёт имитация обфускации данных перед внешней моделью */
  obfuscating?: boolean;
  /** Активная модель внешняя (показываем обфускацию вместо обычной оркестрации) */
  modelExternal?: boolean;
}

const ORCHESTRATION_STEPS = [
  { text: 'Анализирую запрос...', duration: 700 },
  { text: 'Оцениваю сложность задачи...', duration: 900 },
  { text: 'Выбираю оптимальную модель...', duration: 600 },
];
// Total: 2200ms before content appears

function ModelBadge({ modelName }: { modelName: string }) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.75,
        fontSize: 11.5,
        color: 'var(--color-text-muted)',
        mb: 0.75,
      }}
    >
      <CheckCircle2 size={12} color="#22c55e" />
      <span>
        Модель:{' '}
        <Box component="span" sx={{ fontWeight: 700, color: 'var(--color-text-secondary)' }}>
          {modelName}
        </Box>
      </span>
    </Box>
  );
}

function OrchestrationAnimation({ onDone }: { modelName: string; onDone: () => void }) {
  const [step, setStep] = useState(0);
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;

  useEffect(() => {
    if (step >= ORCHESTRATION_STEPS.length) {
      onDoneRef.current();
      return;
    }
    const timer = setTimeout(() => setStep((s) => s + 1), ORCHESTRATION_STEPS[step].duration);
    return () => clearTimeout(timer);
  }, [step]);

  const currentText = step < ORCHESTRATION_STEPS.length ? ORCHESTRATION_STEPS[step].text : null;

  return (
    <Typography
      sx={{
        fontSize: 11,
        color: 'var(--color-text-muted)',
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
      }}
    >
      <CircularProgress size={10} />
      {currentText}
    </Typography>
  );
}

const AI_AVATAR = (
  <Box
    sx={{
      width: 28,
      height: 28,
      borderRadius: '50%',
      background: 'linear-gradient(135deg,#6366f1,#4f46e5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
      color: '#fff',
      fontSize: 10,
      fontWeight: 700,
    }}
  >
    AI
  </Box>
);

const USER_AVATAR = (
  <Box
    sx={{
      width: 28,
      height: 28,
      borderRadius: '50%',
      flexShrink: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: 10,
      fontWeight: 700,
      bgcolor: '#e5e7eb',
      color: 'var(--color-text-secondary)',
    }}
  >
    Вы
  </Box>
);

const MSG_STYLES = {
  ai: {
    bgcolor: 'var(--color-surface)',
    border: '1px solid var(--color-border)',
    borderRadius: '12px 12px 12px 4px',
    color: 'var(--color-text-secondary)',
  },
  user: {
    bgcolor: 'rgba(59,139,255,0.08)',
    border: '1px solid var(--color-primary-light)',
    borderRadius: '12px 12px 4px 12px',
    color: 'var(--color-text-primary)',
  },
};

export default function ChatPanel({
  sessionStatus,
  messages,
  streamingMessage,
  displayedContent,
  progressLabel,
  isLoading,
  onSendMessage,
  onStopGeneration,
  modelName,
  obfuscating = false,
  modelExternal = false,
}: ChatPanelProps) {
  const [text, setText] = useState('');
  const [orchDone, setOrchDone] = useState(false);
  const [showBlock, setShowBlock] = useState(false);
  const prevIsLoadingRef = useRef(false);
  const bottomRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  // Прилипание к низу: автоскролл активен, только пока пользователь у нижнего края.
  // Стоит ему проскроллить вверх — автоскролл отключается и его не тянет обратно вниз.
  const stickToBottomRef = useRef(true);
  const isDisabled = sessionStatus === 'idle';

  // ── Авто-рост инпута под содержимое (как в ChatGPT) ──
  const INPUT_MIN_H = 40; // высота на одну строку
  const INPUT_AUTO_MAX_H = 200; // предел роста, дальше — внутренний скролл
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(Math.max(el.scrollHeight, INPUT_MIN_H), INPUT_AUTO_MAX_H)}px`;
  }, [text]);

  // Для внешней модели сначала идёт «обфускация данных» (7–10 c), затем вывод ответа.
  const revealReady = modelExternal ? !obfuscating : orchDone;

  // Open block on new request; reset animation
  useEffect(() => {
    const wasLoading = prevIsLoadingRef.current;
    prevIsLoadingRef.current = isLoading;
    if (!wasLoading && isLoading) {
      setOrchDone(false);
      setShowBlock(true);
    }
  }, [isLoading]);

  // Close block only after reveal phase finished AND loading done
  useEffect(() => {
    if (revealReady && !isLoading) {
      const t = setTimeout(() => setShowBlock(false), 300);
      return () => clearTimeout(t);
    }
  }, [revealReady, isLoading]);

  // Запрос завершился без ответа ИИ (ошибка отправки, напр. 422 «загрузите повторно»,
  // или error-событие стрима) — гасим блок «думает» сразу, не дожидаясь анимации
  useEffect(() => {
    const lastIsAi = messages.length > 0 && messages[messages.length - 1].role === 'AI';
    if (showBlock && !isLoading && !displayedContent && !lastIsAi) {
      setShowBlock(false);
      setOrchDone(false);
    }
  }, [showBlock, isLoading, displayedContent, messages]);

  // Запрос завершился без ответа ИИ (ошибка отправки, напр. 422 «загрузите повторно»,
  // или error-событие стрима) — гасим блок «думает» сразу, не дожидаясь анимации
  useEffect(() => {
    const lastIsAi = messages.length > 0 && messages[messages.length - 1].role === 'AI';
    if (showBlock && !isLoading && !displayedContent && !lastIsAi) {
      setShowBlock(false);
      setOrchDone(false);
    }
  }, [showBlock, isLoading, displayedContent, messages]);

  // While orch block is visible and last message is AI — hide it from list to avoid duplication
  const displayMessages =
    showBlock && messages.length > 0 && messages[messages.length - 1].role === 'AI'
      ? messages.slice(0, -1)
      : messages;

  // Отслеживаем, у нижнего ли края пользователь (порог 80px), чтобы не мешать ручному скроллу.
  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    stickToBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
  };

  useEffect(() => {
    if (!stickToBottomRef.current) return;
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [displayMessages, isLoading, displayedContent]);

  const send = async () => {
    if (!text.trim() || isDisabled || isLoading) return;
    const msg = text.trim();
    setText('');
    // Отправка своего сообщения — всегда возвращаемся вниз.
    stickToBottomRef.current = true;
    await onSendMessage(msg);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <Box
      sx={{ flex: 1, display: 'flex', flexDirection: 'column', bgcolor: '#f8f9fb', minWidth: 0 }}
    >
      <Box
        ref={scrollRef}
        onScroll={handleScroll}
        sx={{
          flex: 1,
          overflowY: 'auto',
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 1.25,
        }}
      >
        {displayMessages.length === 0 && !streamingMessage && !isLoading && (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
            {AI_AVATAR}
            <Box
              sx={{
                ...MSG_STYLES.ai,
                px: 1.75,
                py: 1.25,
                maxWidth: '75%',
                fontSize: 13,
                lineHeight: 1.55,
              }}
            >
              {sessionStatus === 'idle'
                ? 'Выберите файлы на вкладке «Файлы» и нажмите «Запустить сценарий».'
                : 'Загрузите документ и задайте вопрос — я готов помочь.'}
            </Box>
          </Box>
        )}

        {displayMessages.map((msg, i) => (
          <Box
            key={i}
            sx={{
              display: 'flex',
              gap: 1,
              alignItems: 'flex-start',
              flexDirection: msg.role === 'User' ? 'row-reverse' : 'row',
            }}
          >
            {msg.role === 'AI' ? AI_AVATAR : USER_AVATAR}
            <Box
              sx={{
                px: 1.75,
                py: 1.25,
                maxWidth: '75%',
                minWidth: 0,
                overflowX: 'auto',
                fontSize: 13,
                lineHeight: 1.55,
                ...(msg.role === 'AI' ? MSG_STYLES.ai : MSG_STYLES.user),
              }}
            >
              {msg.role === 'AI' && msg.modelName && <ModelBadge modelName={msg.modelName} />}
              {msg.role === 'AI' ? (
                <ChatMarkdown content={msg.content} />
              ) : (
                // Ввод пользователя — обычный текст: сохраняем переносы строк и не
                // прогоняем через markdown (иначе одиночный \n схлопывается).
                <Box sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{msg.content}</Box>
              )}
              {msg.created_at && (
                <Box
                  sx={{
                    mt: 0.5,
                    fontSize: 10,
                    opacity: 0.6,
                    textAlign: 'right',
                    userSelect: 'none',
                  }}
                >
                  {formatChatMessageTime(msg.created_at)}
                </Box>
              )}
            </Box>
          </Box>
        ))}

        {showBlock &&
          (() => {
            // Content to show: live streaming or (if already done) last AI message
            const lastAiContent =
              !isLoading && messages.length > 0 && messages[messages.length - 1].role === 'AI'
                ? messages[messages.length - 1].content
                : null;
            const content = displayedContent || lastAiContent || '';

            return (
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                {AI_AVATAR}
                <Box
                  sx={{
                    px: 1.75,
                    py: 1.25,
                    maxWidth: '75%',
                    minWidth: 0,
                    overflowX: 'auto',
                    fontSize: 13,
                    lineHeight: 1.55,
                    ...MSG_STYLES.ai,
                  }}
                >
                  {!revealReady ? (
                    modelExternal ? (
                      <ObfuscationIndicator />
                    ) : (
                      <OrchestrationAnimation
                        modelName={modelName ?? ''}
                        onDone={() => setOrchDone(true)}
                      />
                    )
                  ) : (
                    <>
                      {modelName && <ModelBadge modelName={modelName} />}
                      {content ? (
                        <>
                          {progressLabel && (
                            <Typography
                              sx={{
                                fontSize: 11,
                                color: 'var(--color-text-muted)',
                                mb: 0.5,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                              }}
                            >
                              <CircularProgress size={10} />
                              {progressLabel}
                            </Typography>
                          )}
                          <ChatMarkdown content={content} />
                        </>
                      ) : (
                        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                          <CircularProgress size={14} sx={{ color: 'var(--color-primary)' }} />
                          <Typography sx={{ fontSize: 12, color: 'var(--color-text-muted)' }}>
                            Генерирую ответ...
                          </Typography>
                        </Box>
                      )}
                    </>
                  )}
                </Box>
              </Box>
            );
          })()}

        <div ref={bottomRef} />
      </Box>

      <Box
        sx={{
          px: 2,
          py: 1.5,
          bgcolor: 'var(--color-surface)',
          borderTop: '1px solid var(--color-border)',
          display: 'flex',
          gap: 1.25,
          alignItems: 'flex-end',
        }}
      >
        <Box sx={{ flex: 1, position: 'relative' }}>
          <Box
            component="textarea"
            ref={textareaRef}
            value={text}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setText(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={isDisabled}
            placeholder={isDisabled ? 'Сначала запустите сценарий...' : 'Задайте вопрос...'}
            rows={1}
            sx={{
              width: '100%',
              resize: 'none',
              border: '1.5px solid',
              borderColor: 'var(--color-border)',
              borderRadius: 'var(--radius-md)',
              px: 1.5,
              py: 1,
              fontSize: 13,
              lineHeight: 1.5,
              fontFamily: 'inherit',
              color: isDisabled ? 'var(--color-text-muted)' : 'var(--color-text-primary)',
              bgcolor: isDisabled ? '#f3f4f6' : '#f9fafb',
              outline: 'none',
              cursor: isDisabled ? 'not-allowed' : 'text',
              '&:focus': isDisabled
                ? {}
                : { borderColor: 'var(--color-primary)', bgcolor: 'var(--color-surface)' },
              transition: 'border-color 0.15s, background 0.15s',
              minHeight: INPUT_MIN_H,
              overflowY: 'auto',
              display: 'block',
              boxSizing: 'border-box',
            }}
          />
          {isDisabled && (
            <Box
              sx={{
                position: 'absolute',
                right: 10,
                top: '50%',
                transform: 'translateY(-50%)',
                color: 'var(--color-text-muted)',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <Lock size={13} />
            </Box>
          )}
        </Box>
        {isLoading ? (
          <Box
            component="button"
            type="button"
            aria-label="Остановить генерацию"
            title="Остановить генерацию"
            onClick={() => onStopGeneration?.()}
            sx={{
              width: 36,
              height: 36,
              bgcolor: '#ef4444',
              borderRadius: 'var(--radius-md)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              flexShrink: 0,
              border: 'none',
              padding: 0,
              '&:hover': { bgcolor: '#dc2626' },
              transition: 'background 0.15s',
            }}
          >
            <Square size={13} color="#fff" fill="#fff" />
          </Box>
        ) : (
          <Box
            onClick={send}
            sx={{
              width: 36,
              height: 36,
              bgcolor: isDisabled ? '#e5e7eb' : 'var(--color-primary)',
              borderRadius: 'var(--radius-md)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: isDisabled ? 'not-allowed' : 'pointer',
              flexShrink: 0,
              '&:hover': isDisabled ? {} : { bgcolor: 'var(--color-primary-dark)' },
              transition: 'background 0.15s',
            }}
          >
            <Send size={15} color={isDisabled ? '#9ca3af' : '#fff'} />
          </Box>
        )}
      </Box>
    </Box>
  );
}
